import { useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export function useApi() {
  const { token, logout, viewingAsUser, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const request = useCallback(async (method, endpoint, data = null, options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      };

      // If admin is viewing as another user, pass that user's ID in header
      if (isAdmin && viewingAsUser && viewingAsUser.id) {
        headers['X-View-As-User-Id'] = viewingAsUser.id;
      }

      const config = {
        method,
        url: `${API_URL}${endpoint}`,
        headers,
        ...options,
      };

      if (data) {
        if (method.toLowerCase() === 'get') {
          config.params = data;
        } else {
          config.data = data;
        }
      }

      const response = await axios(config);
      return response.data;
    } catch (err) {
      // Handle validation errors (Pydantic returns array of errors)
      let message = 'An error occurred';
      const detail = err.response?.data?.detail;
      
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error - extract first error message
        message = detail[0]?.msg || detail[0]?.message || JSON.stringify(detail[0]);
      } else if (detail && typeof detail === 'object') {
        message = detail.msg || detail.message || JSON.stringify(detail);
      } else if (err.message) {
        message = err.message;
      }
      
      setError(message);
      
      if (err.response?.status === 401) {
        logout();
      }
      
      // Create a proper Error object with the message
      const error = new Error(message);
      error.status = err.response?.status;
      error.data = err.response?.data;
      throw error;
    } finally {
      setLoading(false);
    }
  }, [token, logout, viewingAsUser, isAdmin]);

  const get = useCallback((endpoint, params) => request('get', endpoint, params), [request]);
  const post = useCallback((endpoint, data) => request('post', endpoint, data), [request]);
  const put = useCallback((endpoint, data) => request('put', endpoint, data), [request]);
  const patch = useCallback((endpoint, data) => request('patch', endpoint, data), [request]);
  const del = useCallback((endpoint) => request('delete', endpoint), [request]);

  const uploadFile = useCallback(async (endpoint, file) => {
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = {
        'Content-Type': 'multipart/form-data',
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      // If admin is viewing as another user, pass that user's ID in header
      if (isAdmin && viewingAsUser && viewingAsUser.id) {
        headers['X-View-As-User-Id'] = viewingAsUser.id;
      }

      const response = await axios.post(`${API_URL}${endpoint}`, formData, { headers });

      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Upload failed';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, [token, viewingAsUser, isAdmin]);

  // Upload FormData directly (for more complex uploads)
  const upload = useCallback(async (endpoint, formData) => {
    setLoading(true);
    setError(null);

    try {
      const headers = {
        'Content-Type': 'multipart/form-data',
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      // If admin is viewing as another user, pass that user's ID in header
      if (isAdmin && viewingAsUser && viewingAsUser.id) {
        headers['X-View-As-User-Id'] = viewingAsUser.id;
      }

      const response = await axios.post(`${API_URL}${endpoint}`, formData, { headers });

      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Upload failed';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, [token, viewingAsUser, isAdmin]);

  return { get, post, put, patch, del, delete: del, uploadFile, upload, loading, error };
}
